import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileType2 } from 'lucide-react';
import MediaList from '../components/FileUpload/MediaList';
import { getMediaFiles } from '../services/storage';
import { MediaFile, FileType } from '../types/media';
import { factoryList } from '../data/factories';

const fileTypes: { id: FileType; name: string }[] = [
  { id: 'general_info', name: 'Information générale' },
  { id: 'monthly_meeting', name: 'Réunion mensuelle' },
  { id: 'health_safety', name: 'Santé sécurité' },
  { id: 'shutdown', name: 'Shutdown' },
  { id: 'vs', name: 'VS' }
];

const TypePage: React.FC = () => {
  const { typeId } = useParams<{ typeId: string }>();
  const navigate = useNavigate();
  
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFactory, setSelectedFactory] = useState<string>('all');
  
  const fileType = fileTypes.find(type => type.id === typeId);
  
  // Fetch media files for all factories of this type
  const loadMediaFiles = async () => {
    if (!typeId) return;
    
    setIsLoading(true);
    try {
      const allFiles = await Promise.all(
        factoryList.map(factory => getMediaFiles(factory.id))
      );
      
      // Filter files by type
      const filteredFiles = allFiles
        .flat()
        .filter(file => file.fileType === typeId);
      
      setFiles(filteredFiles);
    } catch (error) {
      console.error('Error loading media files:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    if (!fileType) {
      navigate('/');
      return;
    }
    
    loadMediaFiles();
  }, [typeId, fileType, navigate]);

  // Filter files by selected factory
  const filteredFiles = selectedFactory === 'all'
    ? files
    : files.filter(file => file.factoryId === selectedFactory);

  return (
    <div className="animate-fade-in">
      <div className="flex items-center mb-6">
        <button 
          onClick={() => navigate('/')}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 mr-2"
          aria-label="Retour"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-200" />
        </button>
        
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {fileType?.name}
        </h1>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="h-12 bg-primary flex items-center justify-between px-4">
          <div className="flex items-center">
            <FileType2 size={20} className="text-white mr-2" />
            <h2 className="text-base font-medium text-white">
              Fichiers disponibles
            </h2>
          </div>
          
          <select
            value={selectedFactory}
            onChange={(e) => setSelectedFactory(e.target.value)}
            className="px-3 py-1 rounded-lg text-sm font-medium bg-white/10 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/30"
          >
            <option value="all">Toutes les usines</option>
            {factoryList.map(factory => (
              <option key={factory.id} value={factory.id}>
                {factory.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="p-4">
          <MediaList 
            files={filteredFiles} 
            isLoading={isLoading} 
            onDelete={loadMediaFiles}
          />
        </div>
      </div>
    </div>
  );
};

export default TypePage;